import React, { useState, useCallback } from "react";
import { useAppDispatch, useAppSelector } from "../../../../store/hooks";
import { selectors } from "../../../../store/selectors/shipBuilderSelectors";
import { DriveType } from "../../../../types/drive";
import { formatNumber, formatCurrency } from "../../../../utils/formatting";
import { setDriveWithValidation } from "../../../../store/actions/shipBuilderActions";
import { DRIVE_POTENTIAL } from "../../../../constants/drives";
import { selectIsBreakaway, selectBreakawayHull } from "../../../../store/selectors/breakawayHullSelectors";
import { updateCombinedPerformance, setActiveSection } from "../../../../store/features/breakawayHullSlice";
import { updateBreakawaySection } from "../../../../store/features/breakawayHullSlice";
import { SectionTabs } from "../breakaway/SectionTabs";
import { BreakawaySection } from "../../../../types/breakawayHull";
import { MultipleDriveManager } from "./MultipleDriveManager";

/**
 * Interface for drive rating options in dropdowns
 */
interface DriveRatingOption {
  rating: number;
  label: string;
  tons: number;
  cost: number;
  power: number;
  techLevel: number;
}

/**
 * Interface for combined performance configuration
 */
interface CombinedPerformance {
  maneuver: number;
  jump: number;
  reaction: number;
}



/**
 * Custom hook for drive selection logic
 */
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const useDriveSelection = (techLevel: number, tlOverrideActive: boolean) => {
  const dispatch = useAppDispatch();
  const drives = useAppSelector(selectors.selectDrives) || [];
  const shipSize = useAppSelector(selectors.selectShipSize);

  const handleDriveChange = (type: DriveType, rating: number | "none") => {
    dispatch(setDriveWithValidation({ type, rating }));
  };

  const getDriveOptions = (type: DriveType): DriveRatingOption[] => {
    let driveArray;
    if (type === DriveType.MANEUVER) {
      driveArray = DRIVE_POTENTIAL.MANEUVER;
    } else if (type === DriveType.REACTION) {
      driveArray = DRIVE_POTENTIAL.REACTION;
    } else if (type === DriveType.JUMP) {
      driveArray = DRIVE_POTENTIAL.JUMP;
    } else {
      return [];
    }

    return driveArray
      .filter((row) => tlOverrideActive || row.techLevel <= techLevel)
      .map((row) => ({
        rating: row.rating,
        label: type === DriveType.JUMP ? `Jump-${row.rating}` : `${row.rating}G`,
        tons: row.percentOfHull * (shipSize as number) / 100,
        cost: row.percentOfHull * (shipSize as number) / 100 * 0.5,
        power: row.percentOfHull * (shipSize as number) / 100 * 0.1,
        techLevel: row.techLevel,
      }));
  };

  const getCurrentDrive = (type: DriveType) => {
    if (!drives || !Array.isArray(drives)) {
      return undefined;
    }
    return drives.find((d) => d.type === type);
  };

  return {
    handleDriveChange,
    getDriveOptions,
    getCurrentDrive,
  };
};

// Drive percentage lookup tables based on Traveller rules
const MANEUVER_DRIVE_PERCENTAGES: Record<number, number> = {
  0: 0.5, 1: 1, 2: 2, 3: 3, 4: 4, 5: 5, 6: 6, 7: 7, 8: 8,
  9: 9, 10: 10, 11: 11, 12: 12, 13: 13, 14: 14, 15: 15, 16: 16
};

const REACTION_DRIVE_PERCENTAGES: Record<number, number> = {
  0: 1, 1: 2, 2: 4, 3: 6, 4: 8, 5: 10, 6: 12, 7: 14, 8: 16,
  9: 18, 10: 20, 11: 22, 12: 24, 13: 26, 14: 28, 15: 30, 16: 32
};

const JUMP_DRIVE_PERCENTAGES: Record<number, number> = {
  1: 2.5, 2: 5, 3: 7.5, 4: 10, 5: 12.5, 6: 15, 7: 17.5, 8: 20, 9: 22.5
};

/**
 * Helper function to get drive percentage based on type and rating
 * @param type - The drive type (maneuver, jump, reaction)
 * @param rating - The drive rating
 * @returns The percentage of hull tonnage required
 */
const getDrivePercentage = (type: DriveType, rating: number): number => {
  if (type === DriveType.MANEUVER) {
    return MANEUVER_DRIVE_PERCENTAGES[rating] || 0;
  }
  if (type === DriveType.REACTION) {
    return REACTION_DRIVE_PERCENTAGES[rating] || 0;
  }
  if (type === DriveType.JUMP) {
    return JUMP_DRIVE_PERCENTAGES[rating] || 0;
  }
  return 0;
};

/**
 * Calculates drive tonnage based on type, rating, and hull size
 * @param type - The drive type
 * @param rating - The drive rating
 * @param hullSize - The hull size in tons
 * @returns The drive tonnage required
 */
const calculateDriveTonnage = (type: DriveType, rating: number, hullSize: number): number => {
  if (rating === -1) return 0; // No drive
  const percentage = getDrivePercentage(type, rating);
  return (percentage / 100) * hullSize;
};

/**
 * Calculates drive power consumption based on type, rating, and hull size
 * @param type - The drive type
 * @param rating - The drive rating
 * @param hullSize - The hull size in tons
 * @returns The power consumption in Power units
 */
const calculateDrivePower = (type: DriveType, rating: number, hullSize: number): number => {
  if (rating === -1) return 0; // No drive
  const tonnage = calculateDriveTonnage(type, rating, hullSize);
  if (type === DriveType.MANEUVER || type === DriveType.JUMP) {
    return tonnage * 0.1; // 0.1 Power per ton for maneuver and jump drives
  }
  return 0; // Reaction drives don't consume power
};

/**
 * Finds the closest drive rating for a given tonnage and hull size
 * @param type - The drive type
 * @param tonnage - The target tonnage
 * @param hullSize - The hull size in tons
 * @returns The closest drive rating
 */
const findDriveRatingForTonnage = (type: DriveType, tonnage: number, hullSize: number): number => {
  if (tonnage <= 0) return -1; // No drive
  
  const percentages = type === DriveType.MANEUVER ? MANEUVER_DRIVE_PERCENTAGES :
                     type === DriveType.REACTION ? REACTION_DRIVE_PERCENTAGES :
                     JUMP_DRIVE_PERCENTAGES;
  
  const targetPercentage = (tonnage / hullSize) * 100;
  let closestRating = -1;
  let closestDiff = Infinity;
  
  Object.entries(percentages).forEach(([rating, percentage]) => {
    const diff = Math.abs(percentage - targetPercentage);
    if (diff < closestDiff) {
      closestDiff = diff;
      closestRating = parseInt(rating);
    }
  });
  
  return closestRating;
};

/**
 * Main drive selector component for ship configuration
 * Handles both standard and breakaway hull drive configurations
 */
export const DriveSelector: React.FC = () => {
  const dispatch = useAppDispatch();
  const isBreakaway = useAppSelector(selectIsBreakaway);
  const breakawayHull = useAppSelector(selectBreakawayHull);
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const techLevel = useAppSelector((state) => state.shipBuilder.techLevel) as number;
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const tlOverrideActive = useAppSelector(selectors.selectTLOverrideActive);
  const [distributionMode, setDistributionMode] = useState<'auto' | 'manual'>('auto');
  const [manualTonnageInputs, setManualTonnageInputs] = useState<Record<string, Record<string, string>>>({});

  /**
   * Handles changes to combined performance targets
   * @param field - The performance field being changed
   * @param value - The new value (number or 'none')
   */
  const handleTargetChange = (field: 'maneuver' | 'jump' | 'reaction', value: number | 'none') => {
    const numericValue = value === 'none' ? -1 : value;
    
    dispatch(updateCombinedPerformance({
      ...breakawayHull.combinedPerformance,
      [field]: numericValue,
    }));

    // If in auto mode, redistribute drives automatically
    if (distributionMode === 'auto') {
      const newPerformance = {
        ...breakawayHull.combinedPerformance,
        [field]: numericValue,
      };
      distributeDrivesAutomatically(newPerformance);
    }
  };

  /**
   * Automatically distributes drive tonnage across breakaway sections based on size proportions
   * @param performance - The combined performance configuration
   * @param changedField - Optional field that was changed (for optimization)
   * @param originalValue - Optional original value before change
   */
  const distributeDrivesAutomatically = useCallback((performance: CombinedPerformance, changedField?: string, originalValue?: number | 'none') => {
    if (!breakawayHull.sections.length) return;

    const totalShipSize = breakawayHull.sections.reduce((sum: number, section: BreakawaySection) => sum + section.size, 0);

    breakawayHull.sections.forEach((section: BreakawaySection) => {
      const sectionRatio = section.size / totalShipSize;
      const updatedDrives = { ...section.drives };

      // Calculate tonnage for each drive type
      const maneuverTons = performance.maneuver === -1 ? 0 : calculateDriveTonnage(DriveType.MANEUVER, performance.maneuver, totalShipSize) * sectionRatio;
      const jumpTons = performance.jump === -1 ? 0 : calculateDriveTonnage(DriveType.JUMP, performance.jump, totalShipSize) * sectionRatio;
      const reactionTons = performance.reaction === -1 ? 0 : calculateDriveTonnage(DriveType.REACTION, performance.reaction, totalShipSize) * sectionRatio;

      // Calculate ratings for this section based on assigned tonnage
      const maneuverRating = maneuverTons > 0 ? findDriveRatingForTonnage(DriveType.MANEUVER, maneuverTons, section.size) : -1;
      const jumpRating = jumpTons > 0 ? findDriveRatingForTonnage(DriveType.JUMP, jumpTons, section.size) : -1;
      const reactionRating = reactionTons > 0 ? findDriveRatingForTonnage(DriveType.REACTION, reactionTons, section.size) : -1;

      // Update drives with proper typing
      updatedDrives.maneuver = maneuverRating !== -1 ? {
        type: DriveType.MANEUVER,
        rating: maneuverRating,
        techLevel: 7,
        description: `Maneuver-${maneuverRating} (${maneuverTons.toFixed(1)} tons)`,
      } : null;

      updatedDrives.jump = jumpRating !== -1 ? {
        type: DriveType.JUMP,
        rating: jumpRating,
        techLevel: 7,
        description: `Jump-${jumpRating} (${jumpTons.toFixed(1)} tons)`,
      } : null;

      updatedDrives.reaction = reactionRating !== -1 ? {
        type: DriveType.REACTION,
        rating: reactionRating,
        techLevel: 7,
        description: `Reaction-${reactionRating} (${reactionTons.toFixed(1)} tons)`,
      } : null;

      dispatch(updateBreakawaySection({
        sectionId: section.id,
        section: {
          ...section,
          drives: updatedDrives,
        },
      }));
    });
  }, [breakawayHull.sections, dispatch]);

  /**
   * Placeholder for manual drive distribution (to be implemented)
   * @param performance - The combined performance configuration
   */
  const distributeDrivesManually = (performance: CombinedPerformance) => {
    if (!breakawayHull.sections.length) return;

    const totalShipSize = breakawayHull.sections.reduce((sum: number, section: BreakawaySection) => sum + section.size, 0);

    // For now, just use automatic distribution
    // TODO: Implement manual distribution logic
    breakawayHull.sections.forEach((section: BreakawaySection) => {
      const sectionRatio = section.size / totalShipSize;
      const updatedDrives = { ...section.drives };

      // Calculate tonnage for each drive type
      const maneuverTons = performance.maneuver === -1 ? 0 : calculateDriveTonnage(DriveType.MANEUVER, performance.maneuver, totalShipSize) * sectionRatio;
      const jumpTons = performance.jump === -1 ? 0 : calculateDriveTonnage(DriveType.JUMP, performance.jump, totalShipSize) * sectionRatio;
      const reactionTons = performance.reaction === -1 ? 0 : calculateDriveTonnage(DriveType.REACTION, performance.reaction, totalShipSize) * sectionRatio;

      // Calculate ratings for this section based on assigned tonnage
      const maneuverRating = maneuverTons > 0 ? findDriveRatingForTonnage(DriveType.MANEUVER, maneuverTons, section.size) : -1;
      const jumpRating = jumpTons > 0 ? findDriveRatingForTonnage(DriveType.JUMP, jumpTons, section.size) : -1;
      const reactionRating = reactionTons > 0 ? findDriveRatingForTonnage(DriveType.REACTION, reactionTons, section.size) : -1;

      // Update drives
      updatedDrives.maneuver = maneuverRating !== -1 ? {
        type: DriveType.MANEUVER,
        rating: maneuverRating,
        techLevel: 7,
        description: `Maneuver-${maneuverRating} (${maneuverTons.toFixed(1)} tons)`,
      } : null;

      updatedDrives.jump = jumpRating !== -1 ? {
        type: DriveType.JUMP,
        rating: jumpRating,
        techLevel: 7,
        description: `Jump-${jumpRating} (${jumpTons.toFixed(1)} tons)`,
      } : null;

      updatedDrives.reaction = reactionRating !== -1 ? {
        type: DriveType.REACTION,
        rating: reactionRating,
        techLevel: 7,
        description: `Reaction-${reactionRating} (${reactionTons.toFixed(1)} tons)`,
      } : null;

      dispatch(updateBreakawaySection({
        sectionId: section.id,
        section: {
          ...section,
          drives: updatedDrives,
        },
      }));
    });
  };

  /**
   * Handles manual tonnage changes for individual sections
   * @param sectionId - The section ID
   * @param driveType - The drive type
   * @param tonnage - The new tonnage value
   */
  const handleManualTonnageChange = (sectionId: string, driveType: DriveType, tonnage: number) => {
    const section = breakawayHull.sections.find((s: BreakawaySection) => s.id === sectionId);
    if (!section) return;

    const rating = tonnage > 0 ? findDriveRatingForTonnage(driveType, tonnage, section.size) : -1;
    const updatedDrives = { ...section.drives };

    const driveConfig = rating !== -1 ? {
      type: driveType,
      rating,
      techLevel: 7,
      description: `${driveType === DriveType.MANEUVER ? 'Maneuver' : driveType === DriveType.JUMP ? 'Jump' : 'Reaction'}-${rating} (${tonnage.toFixed(1)} tons)`,
    } : null;

    if (driveType === DriveType.MANEUVER) {
      updatedDrives.maneuver = driveConfig;
    } else if (driveType === DriveType.JUMP) {
      updatedDrives.jump = driveConfig;
    } else if (driveType === DriveType.REACTION) {
      updatedDrives.reaction = driveConfig;
    }

    dispatch(updateBreakawaySection({
      sectionId,
      section: {
        ...section,
        drives: updatedDrives,
      },
    }));
  };

  /**
   * Gets the current drive tonnage for a section
   * @param sectionId - The section ID
   * @param driveType - The drive type
   * @returns The current tonnage
   */
  const getCurrentDriveTonnage = (sectionId: string, driveType: DriveType) => {
    const section = breakawayHull.sections.find((s: BreakawaySection) => s.id === sectionId);
    if (!section) return 0;

    const drive = driveType === DriveType.MANEUVER ? section.drives.maneuver :
                  driveType === DriveType.JUMP ? section.drives.jump :
                  section.drives.reaction;

    if (!drive) return 0;
    return calculateDriveTonnage(driveType, drive.rating, section.size);
  };

  /**
   * Gets the total available tonnage for a drive type
   * @param driveType - The drive type
   * @returns The total available tonnage
   */
  const getTotalAvailableTonnage = (driveType: DriveType) => {
    const totalShipSize = breakawayHull.sections.reduce((sum: number, section: BreakawaySection) => sum + section.size, 0);
    const combinedPerformance = breakawayHull.combinedPerformance;
    
    if (driveType === DriveType.MANEUVER) {
      return combinedPerformance.maneuver === -1 ? 0 : calculateDriveTonnage(DriveType.MANEUVER, combinedPerformance.maneuver, totalShipSize);
    } else if (driveType === DriveType.JUMP) {
      return combinedPerformance.jump === -1 ? 0 : calculateDriveTonnage(DriveType.JUMP, combinedPerformance.jump, totalShipSize);
    } else if (driveType === DriveType.REACTION) {
      return combinedPerformance.reaction === -1 ? 0 : calculateDriveTonnage(DriveType.REACTION, combinedPerformance.reaction, totalShipSize);
    }
    return 0;
  };

  /**
   * Gets the total used tonnage for a drive type
   * @param driveType - The drive type
   * @returns The total used tonnage
   */
  const getTotalUsedTonnage = (driveType: DriveType) => {
    return breakawayHull.sections.reduce((total: number, section: BreakawaySection) => {
      const drive = driveType === DriveType.MANEUVER ? section.drives.maneuver :
                    driveType === DriveType.JUMP ? section.drives.jump :
                    section.drives.reaction;
      
      if (!drive) return total;
      return total + calculateDriveTonnage(driveType, drive.rating, section.size);
    }, 0);
  };

  /**
   * Gets the display value for manual tonnage inputs
   * @param sectionId - The section ID
   * @param driveType - The drive type
   * @returns The display value
   */
  const getManualTonnageDisplayValue = (sectionId: string, driveType: DriveType) => {
    const inputKey = `${sectionId}-${driveType}`;
    const manualValue = manualTonnageInputs[inputKey]?.[driveType];
    
    if (manualValue !== undefined) {
      return manualValue;
    }
    
    return getCurrentDriveTonnage(sectionId, driveType).toFixed(1);
  };

  /**
   * Handles manual tonnage input changes
   * @param sectionId - The section ID
   * @param driveType - The drive type
   * @param value - The input value
   */
  const handleManualTonnageInputChange = (sectionId: string, driveType: DriveType, value: string) => {
    const inputKey = `${sectionId}-${driveType}`;
    setManualTonnageInputs(prev => ({
      ...prev,
      [inputKey]: {
        ...prev[inputKey],
        [driveType]: value,
      },
    }));

    const numericValue = parseFloat(value);
    if (!isNaN(numericValue)) {
      handleManualTonnageChange(sectionId, driveType, numericValue);
    }
  };

  /**
   * Calculates required power for the combined performance
   * @param performance - The combined performance configuration
   * @returns The required power in Power units
   */
  const calculateRequiredPower = (performance: CombinedPerformance) => {
    if (!breakawayHull.sections.length) return 0;
    
    const totalShipSize = breakawayHull.sections.reduce((sum: number, section: BreakawaySection) => sum + section.size, 0);
    
    const maneuverDrivePower = performance.maneuver === -1 ? 0 : calculateDrivePower(DriveType.MANEUVER, performance.maneuver, totalShipSize);
    const jumpDrivePower = performance.jump === -1 ? 0 : calculateDrivePower(DriveType.JUMP, performance.jump, totalShipSize);
    
    return maneuverDrivePower + jumpDrivePower;
  };

  /**
   * Calculates total drive weight for the combined performance
   * @param performance - The combined performance configuration
   * @returns The total drive weight in tons
   */
  const calculateTotalDriveWeight = (performance: CombinedPerformance) => {
    if (!breakawayHull.sections.length) return 0;
    
    const totalShipSize = breakawayHull.sections.reduce((sum: number, section: BreakawaySection) => sum + section.size, 0);
    
    const totalManeuverTons = performance.maneuver === -1 ? 0 : calculateDriveTonnage(DriveType.MANEUVER, performance.maneuver, totalShipSize);
    const totalJumpTons = performance.jump === -1 ? 0 : calculateDriveTonnage(DriveType.JUMP, performance.jump, totalShipSize);
    const totalReactionTons = performance.reaction === -1 ? 0 : calculateDriveTonnage(DriveType.REACTION, performance.reaction, totalShipSize);
    
    return totalManeuverTons + totalJumpTons + totalReactionTons;
  };

  /**
   * Calculates total drive cost for the combined performance
   * @param performance - The combined performance configuration
   * @returns The total drive cost in credits
   */
  const calculateTotalDriveCost = (performance: CombinedPerformance) => {
    if (!breakawayHull.sections.length) return 0;
    
    const totalShipSize = breakawayHull.sections.reduce((sum: number, section: BreakawaySection) => sum + section.size, 0);
    
    const totalManeuverTons = performance.maneuver === -1 ? 0 : calculateDriveTonnage(DriveType.MANEUVER, performance.maneuver, totalShipSize);
    const totalJumpTons = performance.jump === -1 ? 0 : calculateDriveTonnage(DriveType.JUMP, performance.jump, totalShipSize);
    const totalReactionTons = performance.reaction === -1 ? 0 : calculateDriveTonnage(DriveType.REACTION, performance.reaction, totalShipSize);
    
    // Drive costs using the same multipliers as the main calculation
    const maneuverCost = totalManeuverTons * 2.0; // MCr2 per ton for maneuver drives
    const jumpCost = totalJumpTons * 1.5; // MCr1.5 per ton for jump drives
    const reactionCost = totalReactionTons * 0.2; // MCr0.2 per ton for reaction drives
    
    // Total drive cost
    return Math.round((maneuverCost + jumpCost + reactionCost) * 1000000); // Convert to credits
  };

  /**
   * Calculates maneuver drive power for the combined performance
   * @param performance - The combined performance configuration
   * @returns The maneuver drive power in Power units
   */
  const calculateManeuverDrivePower = (performance: CombinedPerformance) => {
    if (!performance || !breakawayHull.sections.length) return 0;
    const { maneuver = -1 } = performance;
    const totalShipSize = breakawayHull.sections.reduce((sum: number, section: BreakawaySection) => sum + section.size, 0);
    return maneuver === -1 ? 0 : calculateDrivePower(DriveType.MANEUVER, maneuver, totalShipSize);
  };

  /**
   * Calculates maneuver drive cost for the combined performance
   * @param performance - The combined performance configuration
   * @returns The maneuver drive cost in credits
   */
  const calculateManeuverDriveCost = (performance: CombinedPerformance) => {
    if (!performance || !breakawayHull.sections.length) return 0;
    const { maneuver = -1 } = performance;
    const totalShipSize = breakawayHull.sections.reduce((sum: number, section: BreakawaySection) => sum + section.size, 0);
    const totalManeuverTons = maneuver === -1 ? 0 : calculateDriveTonnage(DriveType.MANEUVER, maneuver, totalShipSize);
    return Math.round(totalManeuverTons * 2.0 * 1000000); // MCr2 per ton for maneuver drives
  };

  /**
   * Calculates maneuver drive weight for the combined performance
   * @param performance - The combined performance configuration
   * @returns The maneuver drive weight in tons
   */
  const calculateManeuverDriveWeight = (performance: CombinedPerformance) => {
    if (!performance || !breakawayHull.sections.length) return 0;
    const { maneuver = -1 } = performance;
    const totalShipSize = breakawayHull.sections.reduce((sum: number, section: BreakawaySection) => sum + section.size, 0);
    return maneuver === -1 ? 0 : calculateDriveTonnage(DriveType.MANEUVER, maneuver, totalShipSize);
  };

  /**
   * Calculates jump drive power for the combined performance
   * @param performance - The combined performance configuration
   * @returns The jump drive power in Power units
   */
  const calculateJumpDrivePower = (performance: CombinedPerformance) => {
    if (!performance || !breakawayHull.sections.length) return 0;
    const { jump = -1 } = performance;
    const totalShipSize = breakawayHull.sections.reduce((sum: number, section: BreakawaySection) => sum + section.size, 0);
    return jump === -1 ? 0 : calculateDrivePower(DriveType.JUMP, jump, totalShipSize);
  };

  /**
   * Calculates jump drive cost for the combined performance
   * @param performance - The combined performance configuration
   * @returns The jump drive cost in credits
   */
  const calculateJumpDriveCost = (performance: CombinedPerformance) => {
    if (!performance || !breakawayHull.sections.length) return 0;
    const { jump = -1 } = performance;
    const totalShipSize = breakawayHull.sections.reduce((sum: number, section: BreakawaySection) => sum + section.size, 0);
    const totalJumpTons = jump === -1 ? 0 : calculateDriveTonnage(DriveType.JUMP, jump, totalShipSize);
    return Math.round(totalJumpTons * 1.5 * 1000000); // MCr1.5 per ton for jump drives
  };

  /**
   * Calculates jump drive weight for the combined performance
   * @param performance - The combined performance configuration
   * @returns The jump drive weight in tons
   */
  const calculateJumpDriveWeight = (performance: CombinedPerformance) => {
    if (!performance || !breakawayHull.sections.length) return 0;
    const { jump = -1 } = performance;
    const totalShipSize = breakawayHull.sections.reduce((sum: number, section: BreakawaySection) => sum + section.size, 0);
    return jump === -1 ? 0 : calculateDriveTonnage(DriveType.JUMP, jump, totalShipSize);
  };

  /**
   * Calculates reaction drive power for the combined performance
   * @param performance - The combined performance configuration
   * @returns The reaction drive power in Power units
   */
  const calculateReactionDrivePower = (performance: CombinedPerformance) => {
    if (!performance || !breakawayHull.sections.length) return 0;
    const { reaction = -1 } = performance;
    const totalShipSize = breakawayHull.sections.reduce((sum: number, section: BreakawaySection) => sum + section.size, 0);
    return reaction === -1 ? 0 : calculateDrivePower(DriveType.REACTION, reaction, totalShipSize);
  };

  /**
   * Calculates reaction drive cost for the combined performance
   * @param performance - The combined performance configuration
   * @returns The reaction drive cost in credits
   */
  const calculateReactionDriveCost = (performance: CombinedPerformance) => {
    if (!performance || !breakawayHull.sections.length) return 0;
    const { reaction = -1 } = performance;
    const totalShipSize = breakawayHull.sections.reduce((sum: number, section: BreakawaySection) => sum + section.size, 0);
    const totalReactionTons = reaction === -1 ? 0 : calculateDriveTonnage(DriveType.REACTION, reaction, totalShipSize);
    return Math.round(totalReactionTons * 0.2 * 1000000); // MCr0.2 per ton for reaction drives
  };

  /**
   * Calculates reaction drive weight for the combined performance
   * @param performance - The combined performance configuration
   * @returns The reaction drive weight in tons
   */
  const calculateReactionDriveWeight = (performance: CombinedPerformance) => {
    if (!performance || !breakawayHull.sections.length) return 0;
    const { reaction = -1 } = performance;
    const totalShipSize = breakawayHull.sections.reduce((sum: number, section: BreakawaySection) => sum + section.size, 0);
    return reaction === -1 ? 0 : calculateDriveTonnage(DriveType.REACTION, reaction, totalShipSize);
  };

  /**
   * Generates dropdown options for drive ratings
   * @param type - The drive type
   * @returns Array of dropdown options
   */
  const getDriveRatingOptions = (type: 'maneuver' | 'jump' | 'reaction') => {
    const options = [];
    
    // Add "None" option
    options.push({ value: 'none', label: 'None (No Drive)' });
    
    // Add "0" option for maneuver and reaction (not for jump)
    if (type !== 'jump') {
      options.push({ value: 0, label: '0 (Zero-rated Drive)' });
    }
    
    // Add rating options
    const maxRating = type === 'jump' ? 9 : 16;
    for (let i = 1; i <= maxRating; i++) {
      const label = type === 'jump' ? `Jump-${i}` : `${i}G`;
      options.push({ value: i, label });
    }
    
    return options;
  };

  /**
   * Gets the current dropdown value for a field
   * @param field - The performance field
   * @returns The current dropdown value
   */
  const getCurrentDropdownValue = (field: 'maneuver' | 'jump' | 'reaction') => {
    const value = breakawayHull.combinedPerformance[field];
    if (value === -1) return 'none';
    return value;
  };

  // Keyboard event handler for manual tonnage inputs
  React.useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.key === 'Enter') {
        // Handle Enter key press if needed
      }
    };

    document.addEventListener('keypress', handleKeyPress);
    return () => {
      document.removeEventListener('keypress', handleKeyPress);
    };
  }, []);

  // Initialize active section
  React.useEffect(() => {
    if (isBreakaway && breakawayHull.sections.length > 0) {
      // Find the section with the default active section ID or the first one
      const defaultActiveSection = breakawayHull.sections.find(
        (s: BreakawaySection) => s.id === breakawayHull.activeSectionId
      ) || breakawayHull.sections[0];
      // If no active section ID is set, set the first one as active
      if (!breakawayHull.activeSectionId) {
        dispatch(setActiveSection(defaultActiveSection.id));
      }
    }
  }, [isBreakaway, breakawayHull.sections, breakawayHull.activeSectionId, dispatch]);

  // Event listener for opening combined targets modal
  React.useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (event.ctrlKey && event.key === 't') {
        // Only open if not in manual distribution mode
        if (distributionMode === 'auto') {
          // Trigger automatic distribution when switching to auto mode
          if (breakawayHull.combinedPerformance) {
            distributeDrivesAutomatically(breakawayHull.combinedPerformance);
          }
        }
      }
    };

    document.addEventListener('keydown', handleKeyPress);
    return () => {
      document.removeEventListener('keydown', handleKeyPress);
    };
  }, [distributionMode, breakawayHull.combinedPerformance, distributeDrivesAutomatically]);

  const driveTypes = [DriveType.MANEUVER, DriveType.JUMP, DriveType.REACTION];

  // Legacy useDriveSelection hook - no longer used with MultipleDriveManager
  // const { handleDriveChange, getDriveOptions, getCurrentDrive } = useDriveSelection(techLevel, tlOverrideActive);

  // Legacy renderDriveSelector function - no longer used with MultipleDriveManager
  // const renderDriveSelector = (type: DriveType) => { ... }

  // Render breakaway hull section configuration
  const renderBreakawayConfiguration = () => {
    if (!isBreakaway || !breakawayHull.sections.length) return null;

    return (
      <div className="space-y-6">
        {/* Combined Performance Display with Set Targets Button */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100">Breakaway Hull Drive Configuration</h3>
          </div>

          <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-lg p-4 mb-6">
            <h4 className="text-sm font-semibold text-blue-900 dark:text-blue-200 mb-2">How Breakaway Hull Drives Work</h4>
            <ul className="text-xs text-blue-800 dark:text-blue-300 space-y-1">
              <li>• <strong>Step 1:</strong> Set your desired combined vessel performance targets (above)</li>
              <li>• <strong>Step 2:</strong> The app calculates total drive requirements (weight, cost, power) for the WHOLE ship</li>
              <li>• <strong>Step 3:</strong> Choose distribution mode: Automatic (by section size) or Manual (user-assigned)</li>
              <li>• <strong>Step 4:</strong> The app distributes drive tonnage across sections and calculates individual section ratings</li>
              <li>• <strong>Step 5:</strong> Individual section power is calculated based on assigned tonnage vs section size</li>
            </ul>
          </div>

          {/* Combined Performance Targets Configuration */}
          <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-700 rounded-lg p-4 mb-6">
            <h3 className="text-lg font-semibold text-blue-900 dark:text-blue-200 mb-4">Combined Performance Targets</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {/* Maneuver Drive */}
              <div>
                <label className="block text-sm font-medium text-blue-700 dark:text-blue-300 mb-1">Maneuver (G)</label>
                <select
                  value={getCurrentDropdownValue('maneuver')}
                  onChange={(e) => handleTargetChange('maneuver', e.target.value === 'none' ? 'none' : parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-blue-300 dark:border-blue-600 rounded-md focus:border-blue-500 focus:ring-1 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
                >
                  {getDriveRatingOptions('maneuver').map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <div className="mt-2 space-y-1 text-xs">
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Cost:</span>
                    <span className="font-medium text-gray-900 dark:text-gray-100">{formatCurrency(calculateManeuverDriveCost(breakawayHull.combinedPerformance))}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Weight:</span>
                    <span className="font-medium text-gray-900 dark:text-gray-100">{formatNumber(calculateManeuverDriveWeight(breakawayHull.combinedPerformance))} tons</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Power:</span>
                    <span className="font-medium text-gray-900 dark:text-gray-100">{formatNumber(calculateManeuverDrivePower(breakawayHull.combinedPerformance))} Power</span>
                  </div>
                </div>
              </div>
              
              {/* Jump Drive */}
              <div>
                <label className="block text-sm font-medium text-blue-700 dark:text-blue-300 mb-1">Jump</label>
                <select
                  value={getCurrentDropdownValue('jump')}
                  onChange={(e) => handleTargetChange('jump', e.target.value === 'none' ? 'none' : parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-blue-300 dark:border-blue-600 rounded-md focus:border-blue-500 focus:ring-1 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
                >
                  {getDriveRatingOptions('jump').map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <div className="mt-2 space-y-1 text-xs">
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Cost:</span>
                    <span className="font-medium text-gray-900 dark:text-gray-100">{formatCurrency(calculateJumpDriveCost(breakawayHull.combinedPerformance))}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Weight:</span>
                    <span className="font-medium text-gray-900 dark:text-gray-100">{formatNumber(calculateJumpDriveWeight(breakawayHull.combinedPerformance))} tons</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Power:</span>
                    <span className="font-medium text-gray-900 dark:text-gray-100">{formatNumber(calculateJumpDrivePower(breakawayHull.combinedPerformance))} Power</span>
                  </div>
                </div>
              </div>
              
              {/* Reaction Drive */}
              <div>
                <label className="block text-sm font-medium text-blue-700 dark:text-blue-300 mb-1">Reaction (G)</label>
                <select
                  value={getCurrentDropdownValue('reaction')}
                  onChange={(e) => handleTargetChange('reaction', e.target.value === 'none' ? 'none' : parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-blue-300 dark:border-blue-600 rounded-md focus:border-blue-500 focus:ring-1 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
                >
                  {getDriveRatingOptions('reaction').map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
                <div className="mt-2 space-y-1 text-xs">
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Cost:</span>
                    <span className="font-medium text-gray-900 dark:text-gray-100">{formatCurrency(calculateReactionDriveCost(breakawayHull.combinedPerformance))}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Weight:</span>
                    <span className="font-medium text-gray-900 dark:text-gray-100">{formatNumber(calculateReactionDriveWeight(breakawayHull.combinedPerformance))} tons</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600 dark:text-gray-400">Power:</span>
                    <span className="font-medium text-gray-900 dark:text-gray-100">{formatNumber(calculateReactionDrivePower(breakawayHull.combinedPerformance))} Power</span>
                  </div>
                </div>
              </div>
            </div>
            

          </div>

          {/* Distribution Mode */}
          <div className="bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-4 mb-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">Distribution Mode</h3>
            <div className="space-y-3">
              <label className="flex items-center">
                <input
                  type="radio"
                  value="auto"
                  checked={distributionMode === 'auto'}
                  onChange={() => {
                    setDistributionMode('auto');
                    // Trigger automatic distribution when switching to auto mode
                    if (breakawayHull.combinedPerformance) {
                      distributeDrivesAutomatically(breakawayHull.combinedPerformance);
                    }
                  }}
                  className="mr-2"
                />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Automatic Distribution
                </span>
                <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                  (App divides total drive tonnage proportionally by section size)
                </span>
              </label>
              <label className="flex items-center">
                <input
                  type="radio"
                  value="manual"
                  checked={distributionMode === 'manual'}
                  onChange={() => {
                    setDistributionMode('manual');
                    // Trigger manual distribution when switching to manual mode
                    if (breakawayHull.combinedPerformance) {
                      distributeDrivesManually(breakawayHull.combinedPerformance);
                    }
                  }}
                  className="mr-2"
                />
                <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                  Manual Distribution
                </span>
                <span className="text-xs text-gray-500 dark:text-gray-400 ml-2">
                  (User assigns drive tonnage to each section individually)
                </span>
              </label>
            </div>
            

          </div>

          {/* Section Tabs - Show current distribution */}
          <SectionTabs
            activeSectionId={breakawayHull.activeSectionId || breakawayHull.sections[0]?.id || ''}
            onSectionChange={(id) => {
              dispatch(setActiveSection(id));
            }}
          />

          {/* Current Section Summary */}
          <div className="bg-gray-50 dark:bg-gray-700 border border-gray-200 dark:border-gray-600 rounded-lg p-4 mt-4">
            <h4 className="text-sm font-semibold text-gray-900 dark:text-gray-100 mb-3">
              Current Configuration - {breakawayHull.sections.find((s: BreakawaySection) => s.id === (breakawayHull.activeSectionId || breakawayHull.sections[0]?.id))?.name || 'Unknown Section'}
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              {driveTypes.map((type) => {
                const section = breakawayHull.sections.find((s: BreakawaySection) => s.id === (breakawayHull.activeSectionId || breakawayHull.sections[0]?.id));
                const sectionDrives = section?.drives;
                const drive = type === DriveType.MANEUVER ? sectionDrives?.maneuver :
                             type === DriveType.JUMP ? sectionDrives?.jump :
                             type === DriveType.REACTION ? sectionDrives?.reaction : null;
                
                return (
                  <div key={type} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-600 p-3">
                    <div className="font-medium text-gray-700 dark:text-gray-300">
                      {type === DriveType.MANEUVER ? "Maneuver Drive" :
                       type === DriveType.JUMP ? "Jump Drive" : "Reaction Drive"}
                    </div>
                    
                    {distributionMode === 'manual' ? (
                      // Manual mode: Show tonnage input and calculated rating
                      <div className="space-y-2">
                        <div>
                          <label className="block text-xs text-gray-600 dark:text-gray-400 mb-1">Tonnage:</label>
                          <input
                            type="text"
                            value={getManualTonnageDisplayValue(breakawayHull.activeSectionId || breakawayHull.sections[0]?.id || '', type)}
                            onChange={(e) => handleManualTonnageInputChange(breakawayHull.activeSectionId || breakawayHull.sections[0]?.id || '', type, e.target.value)}
                            onBlur={(e) => {
                              // Ensure we have a valid number on blur
                              const value = parseFloat(e.target.value) || 0;
                              handleManualTonnageChange(breakawayHull.activeSectionId || breakawayHull.sections[0]?.id || '', type, value);
                            }}
                            className="w-full px-2 py-1 text-sm border border-gray-300 dark:border-gray-600 rounded focus:border-blue-500 focus:ring-1 focus:ring-blue-500 dark:bg-gray-700 dark:text-gray-100"
                            placeholder="0.0"
                          />
                        </div>
                        <div className="text-lg font-bold text-gray-900 dark:text-gray-100">
                          Rating: {drive ? `${drive.rating > 0 ? drive.rating : '0'}` : 'None'}
                        </div>
                        {drive && (
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {drive.description}
                          </div>
                        )}
                        
                        {/* Running Total for Manual Distribution */}
                        <div className="mt-2 pt-2 border-t border-gray-200 dark:border-gray-600">
                          <div className="text-xs text-gray-600 dark:text-gray-400 space-y-1">
                            <div className="flex justify-between">
                              <span>Available:</span>
                              <span className="font-medium">{formatNumber(getTotalAvailableTonnage(type))} tons</span>
                            </div>
                            <div className="flex justify-between">
                              <span>Used:</span>
                              <span className="font-medium">{formatNumber(getTotalUsedTonnage(type))} tons</span>
                            </div>
                            <div className="flex justify-between">
                              <span>Remaining:</span>
                              <span className={`font-medium ${getTotalAvailableTonnage(type) - getTotalUsedTonnage(type) < 0 ? 'text-red-600 dark:text-red-400' : 'text-green-600 dark:text-green-400'}`}>
                                {formatNumber(getTotalAvailableTonnage(type) - getTotalUsedTonnage(type))} tons
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ) : (
                      // Automatic mode: Show static rating only
                      <div>
                        <div className="text-lg font-bold text-gray-900 dark:text-gray-100">
                          {drive ? `${drive.rating > 0 ? drive.rating : 'None'}` : 'None'}
                        </div>
                        {drive && (
                          <div className="text-xs text-gray-500 dark:text-gray-400">
                            {drive.description}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
            
            {/* Manual mode instructions */}
            {distributionMode === 'manual' && (
              <div className="mt-4 pt-3 border-t border-gray-300">
                <p className="text-xs text-gray-600">
                  <strong>Manual Distribution Mode:</strong> Enter the tonnage of each drive type for this section. 
                  The system will calculate the resulting drive rating based on the section size and Traveller rules.
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  // Helper to calculate power for a single section
  const calculateSectionPower = (section: BreakawaySection) => {
    const sectionDrives = section.drives;
    const maneuverDrive = sectionDrives?.maneuver;
    const jumpDrive = sectionDrives?.jump;
    const reactionDrive = sectionDrives?.reaction;

    // Calculate power using correct percentages from Traveller rules
    const maneuverPower = calculateDrivePower(DriveType.MANEUVER, maneuverDrive?.rating || 0, section.size);
    const jumpPower = calculateDrivePower(DriveType.JUMP, jumpDrive?.rating || 0, section.size);
    const reactionPower = calculateDrivePower(DriveType.REACTION, reactionDrive?.rating || 0, section.size);
    
    return Math.round(maneuverPower + jumpPower + reactionPower);
  };

  return (
    <div className="drive-selector">
      <h2 className="text-xl font-bold mb-4">Ship Drives</h2>
      
      {/* Breakaway Hulls Configuration */}
      {isBreakaway ? (
        renderBreakawayConfiguration()
      ) : (
        /* Standard Drive Selection - Multiple Drives Support */
        <div className="space-y-6">
          {driveTypes.map((type) => (
            <div key={type}>
              <MultipleDriveManager driveType={type} />
            </div>
          ))}
        </div>
      )}

      {/* Combined Targets Modal */}
      {/* This modal is for breakaway hull combined targets, not standard drive selection */}
      {/* If you want a combined targets modal for standard drives, it would need to be re-implemented */}
      {/* For now, it's removed as per the edit hint to remove TypeScript compliance issues */}
      {/* {showCombinedTargets && (
        <BreakawayCombinedTargetConfig
          isOpen={showCombinedTargets}
          onClose={() => setShowCombinedTargets(false)}
        />
      )} */}

      {/* Sticky Footer - Show section drives and combined totals */}
      {isBreakaway && (
        <div className="fixed bottom-0 left-0 right-0 bg-gray-800 text-white p-3 z-50">
          <div className="max-w-7xl mx-auto">
            {/* Individual Section Rows */}
            {breakawayHull.sections.map((section: BreakawaySection) => {
              const sectionDrives = section.drives;
              const maneuverDrive = sectionDrives?.maneuver;
              const jumpDrive = sectionDrives?.jump;
              const reactionDrive = sectionDrives?.reaction;
              
              return (
                <div key={section.id} className="grid grid-cols-5 gap-4 text-center mb-1">
                  <div>
                    <div className="text-xs text-gray-300">
                      {section.name} MANEUVER <span className="font-bold text-green-400">
                        {maneuverDrive ? `${maneuverDrive.rating}G` : '0G'}
                      </span>
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-300">
                      {section.name} JUMP <span className="font-bold text-blue-400">
                        {jumpDrive ? `Jump-${jumpDrive.rating}` : 'Jump-0'}
                      </span>
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-300">
                      {section.name} REACTION <span className="font-bold text-purple-400">
                        {reactionDrive ? `${reactionDrive.rating}G` : '0G'}
                      </span>
                    </div>
                  </div>
                  <div>
                    <div className="text-xs text-gray-300">
                      {section.name} POWER <span className="font-bold text-yellow-400">
                        {calculateSectionPower(section)} Power
                      </span>
                    </div>
                  </div>
                </div>
              );
            })}
            
            {/* Combined Totals Row */}
            <div className="grid grid-cols-5 gap-4 text-center pt-1 border-t border-gray-600">
              <div>
                <div className="text-xs text-gray-300">
                  COMBINED MANEUVER <span className="text-sm font-bold text-green-400">
                    {breakawayHull.combinedPerformance?.maneuver || 0} G
                  </span>
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-300">
                  COMBINED JUMP <span className="text-sm font-bold text-blue-400">
                    Jump-{breakawayHull.combinedPerformance?.jump || 0}
                  </span>
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-300">
                  COMBINED REACTION <span className="text-sm font-bold text-purple-400">
                    {breakawayHull.combinedPerformance?.reaction || 0} G
                  </span>
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-300">
                  COMBINED POWER <span className="text-sm font-bold text-yellow-400">
                    {calculateRequiredPower(breakawayHull.combinedPerformance)} Power
                  </span>
                </div>
              </div>
              <div>
                <div className="text-xs text-gray-300 space-y-1">
                  <div>
                    TOTAL COST <span className="text-sm font-bold text-orange-400">
                      {formatCurrency(calculateTotalDriveCost(breakawayHull.combinedPerformance))}
                    </span>
                  </div>
                  <div>
                    TOTAL WEIGHT <span className="text-sm font-bold text-cyan-400">
                      {formatNumber(calculateTotalDriveWeight(breakawayHull.combinedPerformance))} tons
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
